import { GET, POST, PATCH, DELETE } from "../config";

/**
 * =================================
 * CATEGORÍAS DE INGRESOS
 * =================================
 */

export const listRevenueCategories = async ({ branch = null } = {}) => {
  const params = new URLSearchParams();
  if (branch) params.append("branch", branch);

  const url = params.toString()
    ? `finance/revenue-categories/?${params.toString()}`
    : `finance/revenue-categories/`;

  const response = await GET(url);
  return response.data;
};

export const createRevenueCategory = async (data) => {
  const response = await POST("finance/revenue-categories/", data);
  return response.data;
};

export const updateRevenueCategory = async (id, data) => {
  const response = await PATCH(`finance/revenue-categories/${id}/`, data);
  return response.data;
};

export const deleteRevenueCategory = async (id) => {
  const response = await DELETE(`finance/revenue-categories/${id}/`);
  return response.data;
};

/**
 * =================================
 * INGRESOS (REVENUES)
 * =================================
 */

export const listRevenues = async ({
  page = 1,
  branch = null,
  category = null,
  status = null,
  revenue_type = null,
  start_date = null,
  end_date = null,
  page_size = 20, // ✅ Unificado con frontend default
} = {}) => {
  // ✅ Validar que page_size sea un número válido
  const validPageSize = Math.max(1, Math.min(200, parseInt(page_size) || 20));
  if (page_size !== validPageSize) {
    console.warn(`[listRevenues] page_size ajustado de ${page_size} a ${validPageSize}`);
  }

  const params = new URLSearchParams();
  params.append("page", page);
  params.append("page_size", validPageSize);

  if (branch) params.append("branch", branch);
  if (category) params.append("category", category);
  if (status) params.append("status", status);
  if (revenue_type) params.append("revenue_type", revenue_type);
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);

  const response = await GET(`finance/revenues/?${params.toString()}`);
  return response.data;
};

export const getRevenue = async (id) => {
  const response = await GET(`finance/revenues/${id}/`);
  return response.data;
};

export const createRevenue = async (data) => {
  const response = await POST("finance/revenues/", data);
  return response.data;
};

export const updateRevenue = async (id, data) => {
  const response = await PATCH(`finance/revenues/${id}/`, data);
  return response.data;
};

export const deleteRevenue = async (id) => {
  const response = await DELETE(`finance/revenues/${id}/`);
  return response.data;
};

/**
 * =================================
 * ACCIONES ESPECÍFICAS DE INGRESOS
 * =================================
 */

export const getRevenuesSummary = async ({ branch = null, start_date = null, end_date = null } = {}) => {
  const params = new URLSearchParams();
  if (branch) params.append("branch", branch);
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);

  const response = await GET(`finance/revenues/summary/?${params.toString()}`);
  return response.data;
};

export const getPendingRevenues = async ({ branch = null } = {}) => {
  const params = new URLSearchParams();
  if (branch) params.append("branch", branch);

  const response = await GET(`finance/revenues/pending/?${params.toString()}`);
  return response.data;
};

/**
 * Obtener ingresos pendientes por cliente (para convenios de pago)
 */
export const getPendingRevenuesByClient = async (clientId) => {
  const params = new URLSearchParams();
  params.append("client", clientId);
  params.append("status", "PENDING");

  const response = await GET(`finance/revenues/?${params.toString()}`);
  return response.data;
};

export const getReceivedRevenues = async ({ branch = null } = {}) => {
  const params = new URLSearchParams();
  if (branch) params.append("branch", branch);

  const response = await GET(`finance/revenues/received/?${params.toString()}`);
  return response.data;
};

export const markRevenueAsReceived = async (id, data = {}) => {
  const response = await POST(`finance/revenues/${id}/mark_received/`, data);
  return response.data;
};

export const markRevenueAsUnpaid = async (id) => {
  const response = await POST(`finance/revenues/${id}/mark_as_unpaid/`, {});
  return response.data;
};

export const updateRevenueStatus = async (id, status) => {
  const response = await POST(`finance/revenues/${id}/update_status/`, {
    status,
  });
  return response.data;
};

/**
 * =================================
 * PAGOS DE INGRESOS (usando Payment unificado)
 * =================================
 */

export const addRevenuePayment = async (revenueId, paymentData) => {
  const response = await POST(
    `finance/revenues/${revenueId}/add_payment/`,
    paymentData
  );
  return response.data;
};

export const getRevenuePayments = async (revenueId) => {
  const response = await GET(`finance/revenues/${revenueId}/payments_list/`);
  return response.data;
};

export const convertRevenueToExpense = async (revenueId, data = {}) => {
  const response = await POST(`finance/revenues/${revenueId}/convert_to_expense/`, data);
  return response.data;
};

/**
 * =================================
 * COMPROBANTES DE INGRESOS
 * =================================
 */

/**
 * Generar boleta PDF formato 80mm con diseño completo
 * Usa el mismo generador que las órdenes (order_boleta_pdf_generator)
 * GET /api/finance/revenues/{id}/generate-boleta-pdf/
 */
export const generateBoletaPdf = async (id) => {
  const token = localStorage.getItem("token");
  const configModule = await import("../config");
  const Axios = configModule.Axios;
  try {
    const response = await Axios.get(`finance/revenues/${id}/generate-boleta-pdf/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
      responseType: 'blob',
    });
    
    // Verificar si la respuesta es realmente un PDF o un error JSON
    // Los errores del backend con responseType: 'blob' vienen como JSON dentro de un Blob
    if (response.data instanceof Blob) {
      // Verificar el Content-Type de la respuesta
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        // Es un error JSON, leerlo como texto
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || errorData.detail || 'Error al generar PDF');
      }
      // Es un PDF válido
      return response.data;
    }
    
    return response.data;
  } catch (error) {
    // Si es un error de axios con respuesta blob que es JSON
    if (error.response && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || errorData.detail || 'Error al generar PDF');
      } catch (parseError) {
        // Si no se puede parsear, usar el mensaje del error original
        const errorMsg = error.message || 'Error al generar PDF';
        throw new Error(errorMsg);
      }
    }
    // Si el error tiene un mensaje, usarlo
    if (error.message) {
      throw error;
    }
    throw new Error('Error al generar PDF');
  }
};

/**
 * Exportar ingresos a Excel
 */
export const exportToExcel = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== null && params[key] !== undefined) {
      queryParams.append(key, params[key]);
    }
  });

  // Usar GET con responseType blob para descargar archivos
  const token = localStorage.getItem("token");
  const configModule = await import("../config");
  const Axios = configModule.Axios;
  const response = await Axios.get(
    `finance/revenues/export-excel/?${queryParams.toString()}`,
    {
      headers: {
        Authorization: `Token ${token}`,
      },
      responseType: "blob",
    }
  );
  return response;
};

/**
 * Descargar comprobante de ingreso
 * Si hay archivo adjunto, lo descarga. Si no, genera PDF automáticamente.
 * GET /api/finance/revenues/{id}/download-voucher/
 */
export const downloadRevenueVoucher = async (id) => {
  const token = localStorage.getItem("token");
  const configModule = await import("../config");
  const Axios = configModule.Axios;
  try {
    const response = await Axios.get(`finance/revenues/${id}/download-voucher/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
      responseType: 'blob',
    });
    
    // Verificar si la respuesta es realmente un PDF o un error JSON
    // Los errores del backend con responseType: 'blob' vienen como JSON dentro de un Blob
    if (response.data instanceof Blob) {
      // Verificar el Content-Type de la respuesta
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        // Es un error JSON, leerlo como texto
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || errorData.detail || 'Error al descargar comprobante');
      }
      // Es un PDF válido
      return response.data;
    }
    
    return response.data;
  } catch (error) {
    // Si es un error de axios con respuesta blob que es JSON
    if (error.response && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || errorData.detail || 'Error al descargar comprobante');
      } catch (parseError) {
        // Si no se puede parsear, usar el mensaje del error original
        const errorMsg = error.message || 'Error al descargar comprobante';
        throw new Error(errorMsg);
      }
    }
    // Si el error tiene un mensaje, usarlo
    if (error.message) {
      throw error;
    }
    throw new Error('Error al descargar comprobante');
  }
};

/**
 * =================================
 * CONSTANTES
 * =================================
 */

export const REVENUE_TYPES = {
  SALE: "Venta",
  SERVICE: "Servicio",
  RENTAL: "Alquiler",
  COMMISSION: "Comisión",
  INVESTMENT: "Inversión",
  REFUND: "Reembolso",
  OTHER: "Otro",
};

export const REVENUE_STATUSES = {
  PENDING: "Pendiente",
  RECEIVED: "Completado",
  CANCELLED: "Cancelado",
  REFUNDED: "Reembolsado",
};

export const REVENUE_CATEGORY_TYPES = {
  SALES: "Ventas",
  SERVICES: "Servicios",
  RENTAL: "Alquiler",
  COMMISSION: "Comisiones",
  INVESTMENT: "Inversiones",
  REFUND: "Reembolsos",
  OTHER: "Otros",
};

/**
 * =================================
 * ANULAR/CANCELAR INGRESOS
 * =================================
 */

export const cancelRevenue = async (id, { reason = "" } = {}) => {
  const response = await POST(`finance/revenues/${id}/cancel/`, { reason });
  return response.data;
};
